import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Button, Text } from '../../../ui';
import { FlexContainer } from '../../../ui/Container';
import { MembershipCategory, WizardFormData } from '../types';

const CategoryCard = styled.div<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${(p) => p.theme.spacing.sm};
  border: 2px solid ${(p) => (p.$isSelected ? p.theme.colors.general.primary : p.theme.colors.border.pya)};
  border-radius: ${(p) => p.theme.radius.md};
  background-color: ${(p) => (p.$isSelected ? 'rgba(20, 118, 255, 0.05)' : p.theme.colors.general.white)};
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 200px;
  max-width: 280px;

  &:hover {
    border-color: ${(p) => p.theme.colors.general.primary};
    box-shadow: ${(p) => p.theme.shadows.sm};
  }

  @media (max-width: ${(p) => p.theme.screen.sm}) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const CategoryIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${(p) => p.theme.spacing.xs};
  font-size: 24px;
`;

const CategoryTitle = styled(Text)`
  font-size: ${(p) => p.theme.font.size.md};
  font-weight: ${(p) => p.theme.font.weight.semibold};
  color: ${(p) => p.theme.colors.general.black};
  margin-bottom: 4px;
`;

const CategoryDescription = styled(Text)`
  font-size: ${(p) => p.theme.font.size.xs};
  color: ${(p) => p.theme.colors.general.textSecondary};
  line-height: 1.4;
`;

const categories: { id: MembershipCategory; title: string; description: string; icon: string; color: string }[] = [
  {
    id: 'member',
    title: 'Member',
    description: 'For yacht crew advancing their career with MCA-approved services',
    icon: '⚓',
    color: '#E3F2FD',
  },
  {
    id: 'partner',
    title: 'Partner',
    description: 'For companies and organizations in the yachting industry',
    icon: '🤝',
    color: '#E8F5E9',
  },
  {
    id: 'non-member',
    title: 'Non-Member',
    description: 'Limited access to features, no membership required',
    icon: '👤',
    color: '#FFF3E0',
  },
];

interface CategoryStepProps {
  onNext: () => void;
}

const CategoryStep: FC<CategoryStepProps> = ({ onNext }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<WizardFormData>();

  const selectedCategory = watch('category');

  const handleSelectCategory = (category: MembershipCategory) => {
    setValue('category', category, { shouldValidate: true });
    // Clear membership type when changing category
    setValue('membershipType', '');
  };

  return (
    <FlexContainer flexDirection="column" gap="md" align="center">
      <FlexContainer flexDirection="column" align="center" gap="xxs">
        <Text weight="semibold" size="lg" style={{ textAlign: 'center' }}>
          How would you like to join PYA?
        </Text>
        <Text color="textSecondary" size="sm" style={{ textAlign: 'center' }}>
          Choose the option that best describes you
        </Text>
      </FlexContainer>

      <FlexContainer gap="sm" justify="center" style={{ flexWrap: 'wrap', width: '100%' }}>
        {categories.map((cat) => (
          <CategoryCard
            key={cat.id}
            $isSelected={selectedCategory === cat.id}
            onClick={() => handleSelectCategory(cat.id)}
          >
            <CategoryIcon $color={cat.color}>{cat.icon}</CategoryIcon>
            <CategoryTitle>{cat.title}</CategoryTitle>
            <CategoryDescription>{cat.description}</CategoryDescription>
          </CategoryCard>
        ))}
      </FlexContainer>

      {errors.category && (
        <Text color="error" size="xs">
          {errors.category.message}
        </Text>
      )}

      <Button type="button" color="primary" onClick={onNext} disabled={!selectedCategory} style={{ minWidth: '200px' }}>
        Continue
      </Button>
    </FlexContainer>
  );
};

export default CategoryStep;
